import {DataObject} from "./DataObject";

export class Resource implements DataObject {
  ID: number;
  org_ID: number;
  type: string;
  hash: string | null;
  hashbytes: string;
  problems: string;
  lastUpdate: Date;
  problemStatus: string;
  prepared: string;
  instructions: any;
  problemNote: string;
  lastUsed: Date;
  /**
   * When the row was created. Added server-side on 2026-09-14
   * (`sql/add_resource_created.sql`) and immutable thereafter.
   *
   * It exists because the upload rate limiter needs to count *creations*, and
   * the only timestamp available before it was `lastUpdate`, which moves on
   * every write -- the asynchronous page-count write, a hash write, an
   * ordinary edit. Counting rows *touched* in the last hour meant a user who
   * had uploaded nothing could be refused an upload for having edited existing
   * resources. `created` is what makes "40 uploads per hour" mean uploads.
   *
   * Arrives as an ISO string over JSON, exactly like `lastUpdate` -- `update`
   * copies primitives straight through and does not revive Dates -- so do not
   * call Date methods on it without constructing one first.
   */
  created: Date;
  /**
   * Pages in the uploaded PDF. 0 until the page-count job has run.
   *
   * This is the one thing the `RESOURCE` / `UPDATE` notification exists to
   * deliver: the CREATE fires before the file is even in S3 and carries
   * `numPages: 0`, and the UPDATE follows once the count and hash are both
   * written (`docs/contracts/shared/Notifications.md` section 9).
   *
   * It was already arriving on the payload and being copied onto the
   * instance by `update()`, which takes any primitive - just undeclared, so
   * nothing could reference it without TypeScript complaining.
   *
   * NOTE `DraftBody` carries its own `numPages`, and that is the one the
   * body spread gates page rendering on. They describe the same PDF and are
   * written by the same job; this one arrives first, over the notification.
   */
  numPages: number;

  constructor (obj) {
    this.ID = 0;
    this.org_ID = 0;
    this.type = '';
    this.hash = '';
    this.hashbytes = '';
    this.problems = '';
    this.lastUpdate = new Date(0);
    this.prepared = '';
    this.problemStatus = '';
    this.problemNote = '';
    this.instructions = {};
    this.lastUsed = new Date(0);
    this.created = new Date(0);
    this.numPages = 0;

    this.lastUpdate = new Date(0);
    if ( obj && obj['ID']) {
      this.ID = obj['ID'];
    }
    this.update(obj);
  }

  getId() {
    return this.ID;
  }

  getOrderValue() {
    return this.ID;
  }

  update (obj: any) {
    if (obj) {
      for (const prop of Object.keys(obj)) {
        if (obj[prop] !== null && (prop !== 'ID') && ( typeof obj[prop] === 'string' || typeof obj[prop] === 'number' || typeof obj[prop] === 'boolean' || obj[prop] instanceof String || obj[prop] instanceof Boolean || obj[prop] instanceof Number || obj[prop] instanceof Date )) {
          this[prop] = obj[prop];
        }
      }
    }
  }

  toJSON() {
    const obj = {};
    for (const prop of Object.keys(this)) {
      // `created` is assigned by the server and immutable, so it is never sent
      // back. This object is also the request body of `POST /resource/upload`,
      // where a brand new Resource carries the constructor default -- the
      // epoch. If the server ever took the client's word for the value, every
      // uploaded row would be stamped 1970 and the upload rate limiter, which
      // counts rows *created* in the last hour, would count none of them.
      if (prop === 'created') { continue; }
      if (this[prop] !== null && ( typeof this[prop] === 'string' || typeof this[prop] === 'number' || typeof this[prop] === 'boolean' || this[prop] instanceof String || this[prop] instanceof Boolean || this[prop] instanceof Number || this[prop] instanceof Date )) {
        obj[prop] = this[prop];
      } else {
        if ( prop === 'instructions' && this.instructions ) {
          obj[prop] = this.instructions;
        }
      }
    }
    return obj;
  }

}
